import type { ReportMetadata } from '../../types';
import type {
    Anhang2Treffer,
    DritterNachPara4,
    FrageAdressat,
    FrageKritikalitaet,
    FrageStatus,
    FrageZielobjekt,
    Gebaeudetyp,
    PflichtArt,
    PflichtEintrag,
    PflichtStand,
    Planungsfrage,
    Planungsphase,
    PlanungStatus,
    ProjektMerkmale,
    SigeSchrittDokumente,
    UnterlagenQuelle,
    UnterlagenStatus,
} from '../../components/planungsphase/types';
import {
    gebaeudetypAus,
    pflichtenStand,
    pruefeAnhang2,
    pruefungWeichtAb,
    sortiereFragen,
} from '../../components/planungsphase/planungLogic';
import {
    baueUnterlagenZeilen,
    type MerkmalRelevanz,
    type ZeilenGruppe,
} from '../../components/planungsphase/unterlagenLogic';
import { istAdresseLeer } from '../../components/project-hub/projectDataHygiene';
import {
    idsFuerNummern,
    istAnhangIINummer,
    type AnhangIINummer,
    type DangerousWorkId,
} from '../../data/anhang2Bruecke';
import { parseDateLocal } from '../dateService';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PROJEKTSTECKBRIEF — die eine Lesestelle fuer alle Folgeunterlagen
 * (docs/planungsphase-automatisierung-plan.md, Stufe 1.2)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Vorankuendigung, SiGe-Plan, A+S-Plan, UfsA, Baustellenordnung und Handbuch
 * haben bisher jede fuer sich getippt, was in Projektdaten und Planungsphase
 * laengst steht. Der Steckbrief leitet alles EINMAL ab und reicht es weiter.
 *
 * ═══ NUR LESEN, NIE SCHREIBEN ═════════════════════════════════════════════
 * Hier wird nichts gespeichert und nichts angelegt. Der Steckbrief ist eine
 * abgeleitete Sicht; die Wahrheit bleibt in `planungsphasen` und in den
 * Projektdaten. Eine Folgeunterlage uebernimmt Werte nur per Klick (Leiste
 * „Aus der Planungsphase", Stufe 3) — nie beim Laden.
 *
 * ═══ JEDE ANGABE TRAEGT WERT, QUELLE UND STAND ════════════════════════════
 *   belegt      — aus der zustaendigen Quelle, verwendbar.
 *   ungeklaert  — fehlt oder stammt nur aus einer Ersatzquelle (`grund` sagt,
 *                 welcher). Ein Leser darf daraus KEINE Entwarnung lesen.
 *   widerspruch — zwei Quellen sagen Verschiedenes (heute: der gespeicherte
 *                 Pruefungs-Schnappschuss passt nicht mehr zu den Merkmalen).
 *
 * Fehlt die Planungsphase, ist alles aus ihr `ungeklaert` mit Grund — nie ein
 * leerer Steckbrief, der wie „alles geklaert" aussieht. Konnte sie nicht
 * GELESEN werden (`lesefehler`), steht das als Grund dort, nicht „noch nicht
 * angelegt" — das sind zwei verschiedene Lagen.
 *
 * ═══ ENTSCHEIDE AUS DEM PLAN ══════════════════════════════════════════════
 *   E1  Ausfuehrungszeitraum = `globalData.executionStartDate/EndDate`.
 *       Rueckfall Projektstart/-ende (`startDate/endDate`) mit sichtbarer
 *       Quelle und Stand `ungeklaert`: der Projektzeitraum kann die
 *       Planungsphase einschliessen, als Bauzeit ist er nur eine Vermutung.
 *   E2  Arbeitstage, Hoechstzahl Beschaeftigte, Personentage, mehrere
 *       Arbeitgeber kommen ALLEIN aus den Merkmalen der Planungsphase. Keine
 *       Ersatzquelle — auch nicht die Vorankuendigung.
 *
 * ═══ LIVE GERECHNET, SCHNAPPSCHUSS NUR VERGLICHEN ═════════════════════════
 * Anhang II und Pflichten werden aus den heutigen Merkmalen gerechnet
 * (`pruefeAnhang2`, `pflichtenStand`). `phase.pruefung` ist der beim letzten
 * Speichern eingefrorene Schnappschuss; weicht er ab, meldet `schnappschuss`
 * den Widerspruch — nie umgekehrt (Falle 1 im Plan).
 *
 * ═══ AUSZUG JE FOLGEUNTERLAGE (`steckbriefAuszug`) ════════════════════════
 * Serialisierbare Teilmenge nur aus WERTEN (keine Quellen, keine Staende),
 * Schluessel rekursiv sortiert, Listen nach Code bzw. Id geordnet. Gleiche
 * Eingabe ergibt byteweise dieselbe Ausgabe — Grundlage fuer das Einfrieren
 * bei Freigabe und den Inhaltsvergleich des Aenderungsdiensts (Stufe 6.1).
 *
 *   Feld                   Vorank.  SiGe-Plan  A+S-Plan  UfsA  Bauordnung  Handbuch
 *   projekt (Nr/Name/Ort)    P1        Kopf       Kopf    Kopf   Kopf        Kopf
 *   bauherr                  P2        Kopf       Kopf    Kopf   Erlassender Kopf
 *   dritter (§ 4)            P4        —          —       —      Erlassender —
 *   baubeschreibung          P3        Grundlagen objekt  Kopf   —           —
 *   bauaufgabe               —         Grundlagen —       —      —           —
 *   ausfuehrung              P6        Plankopf   —       Fertig Geltung     Zeitraeume
 *   umfang                   P7/P9     Kopf       —       —      mehrereAG   —
 *   gebaeudetyp              —         Merkmale   —       Vorlage Merkmale   Merkmale
 *   merkmale (ohne Umfang)   —         Musterz.   Auszug  —      Kapitel     Abgleich
 *   anhang2                  P11       Hinweis    —       —      —           je Gewerk
 *   pflichten                vorank.   sigeplan+  asplan  ufsa   koord.+     koord.+
 *                                      koord.                    sigeplan    sigeplan
 *   fragen (zielobjekt)      vorank.   sigeplan   —       unterlage —        —
 *   unterlagen               —         alle rel.  asplan  ufsa   —           alle rel.
 *
 * `umfang` der Vorankuendigung traegt zusaetzlich `unternehmerOhneBeschaeftigte`
 * (P9); der A+S-Auszug der Merkmale enthaelt `altlastenverdacht` (zweiter
 * Verdachtsweg, E4). `dritterNachPara4` steht NIE im allgemeinen Merkmals-
 * auszug — Personendaten nur dort, wo der Dritte gebraucht wird.
 *
 * Was NICHT hineingehoert: Verteiler, Koordinatoren, Notrufplan, LV — die
 * haben eigene Quellen und eigene Lesewege.
 *
 * ═══ KEIN DOM, KEINE SUPABASE ═════════════════════════════════════════════
 * Diese Datei ist pur und in der Node-Umgebung testbar. `jetztIso` kommt vom
 * Aufrufer (Tag in Ortszeit, `heuteLokalIso`), kein `new Date()` hier.
 */

// ═══════════════════════════════════════════════════════════════════════════
// ANGABE — Wert, Quelle, Stand
// ═══════════════════════════════════════════════════════════════════════════

export type AngabeStand = 'belegt' | 'ungeklaert' | 'widerspruch';

export interface Angabe<T> {
    /** `null` = nichts bekannt. Bei `ungeklaert` KANN ein Ersatzwert stehen (siehe `grund`). */
    wert: T | null;
    /** Kurzer lesbarer Herkunftstext, z. B. „Planungsphase · Merkmale". */
    quelle: string;
    stand: AngabeStand;
    /** Warum nicht `belegt` — Pflicht bei `ungeklaert` und `widerspruch`. */
    grund?: string;
}

/** Herkunftstexte — so erscheinen sie in den Leisten der Folgeunterlagen. */
export const QUELLE = {
    planungGrundlagen: 'Planungsphase · Grundlagen',
    planungMerkmale: 'Planungsphase · Merkmale',
    planungPruefung: 'Planungsphase · Prüfung (live)',
    planungFragen: 'Planungsphase · Fragen',
    planungUnterlagen: 'Planungsphase · Unterlagen',
    projektdaten: 'Projektdaten',
    projektAusfuehrung: 'Projektdaten · Ausführungszeitraum',
    projektLaufzeit: 'Projektdaten · Projektstart/-ende',
    keine: 'keine Quelle',
} as const;

/** Gruende — als Konstanten, damit Oberflaeche und Tests dieselben Worte sehen. */
export const GRUND = {
    keinePhase: 'Noch keine Planungsphase angelegt.',
    phaseUnlesbar: (fehler: string) => `Planungsphase konnte nicht gelesen werden: ${fehler}`,
    nurProjektdaten: 'Nur aus Projektdaten — in der Planungsphase noch nicht erfasst.',
    nichtGepflegt: 'In den Projektdaten nicht gepflegt.',
    projektdatenFehlen: 'Projektdaten liegen nicht vor.',
    merkmalFehlt: 'In den Merkmalen der Planungsphase nicht angegeben.',
    grundlageFehlt: 'In den Grundlagen der Planungsphase nicht erfasst.',
    rueckfallLaufzeit: 'Ausführungszeitraum nicht gepflegt — Projektstart/-ende kann die Planungsphase einschließen.',
    datumUnlesbar: (roh: string) => `Datum „${roh}" ist nicht lesbar.`,
    anhang2Offen: (nummern: readonly number[]) => `Anhang II Nr. ${nummern.join(', ')} noch nicht geklärt.`,
    pruefungNieGespeichert: 'Die Prüfung wurde noch nie festgeschrieben — Grundlagen speichern.',
    pruefungWeichtAb: (tag: string) =>
        `Die gespeicherte Prüfung${tag ? ` vom ${tag}` : ''} passt nicht mehr zu den heutigen Merkmalen — Grundlagen erneut speichern.`,
} as const;

const belegt = <T>(wert: T, quelle: string): Angabe<T> => ({ wert, quelle, stand: 'belegt' });
const ungeklaert = <T>(wert: T | null, quelle: string, grund: string): Angabe<T> =>
    ({ wert, quelle, stand: 'ungeklaert', grund });
const widerspruch = <T>(wert: T | null, quelle: string, grund: string): Angabe<T> =>
    ({ wert, quelle, stand: 'widerspruch', grund });

// ═══════════════════════════════════════════════════════════════════════════
// QUELLEN UND ERGEBNIS
// ═══════════════════════════════════════════════════════════════════════════

/** Was beim Laden nicht gelesen werden konnte — je Quelle, `null` = gelesen. */
export interface SteckbriefLesefehler {
    planungsphase: string | null;
    sigeplan: string | null;
}

export const OHNE_LESEFEHLER: SteckbriefLesefehler = { planungsphase: null, sigeplan: null };

/** Rohstoff des Steckbriefs — zusammengetragen von `ladeSteckbriefQuellen`. */
export interface SteckbriefQuellen {
    /** `report.metadata` des Projekts (enthaelt `globalData` mit den Terminen). */
    metadata: Partial<ReportMetadata> | null;
    phase: Planungsphase | null;
    fragen: readonly Planungsfrage[];
    /** Mitgeltende Dokumente des aktuellen SiGe-Plans — fuer die Unterlagen-Checkliste; leer ohne Plan. */
    sigeSchritte: readonly SigeSchrittDokumente[];
    lesefehler: SteckbriefLesefehler;
}

export interface SteckbriefFrage {
    /** `planungsfragen.id` — fuer den Stempel `markiereUebernommen` aus den
     *  Zielmodulen. Gehoert NICHT in den Auszug (`fragenAuszug`): der
     *  vergleicht Inhalte, und eine Id ist kein Inhalt. */
    id: string;
    code: string;
    frage: string;
    kritikalitaet: FrageKritikalitaet;
    status: FrageStatus;
    antwort: string;
    /** Schon in das Zielmodul uebernommen (`planungsfragen.uebernommen_am`). */
    uebernommen_am: string | null;
}

export interface SteckbriefUnterlage {
    id: string;
    name: string;
    quelle: UnterlagenQuelle;
    gruppe: ZeilenGruppe;
    status: UnterlagenStatus;
    /** Beleg in der Projektablage (`project_documents.id`). */
    dokument_id: string | null;
    relevanz: MerkmalRelevanz;
    /** Merkmal ausdruecklich wahr ODER im SiGe-Plan genannt. */
    erforderlich: boolean;
    /** Bleibt fuer spaetere Arbeiten von Bedeutung (RAB 32) — Kandidat fuer die UfsA. */
    ufsa: boolean;
    /** Vorgeschlagene Art im A+S-Plan Kapitel 9, sonst `null`. */
    asplan: 'grundlage' | 'anlage' | null;
    lieferant: FrageAdressat[];
    /** Arbeitsschritte des SiGe-Plans, die das Dokument nennen. */
    sigeSchritte: string[];
}

/** Lage des gespeicherten Pruefungs-Schnappschusses gegenueber den heutigen Merkmalen. */
export type SchnappschussStand = 'aktuell' | 'weicht_ab' | 'fehlt';

export interface ProjektSteckbrief {
    /** Tag (YYYY-MM-DD, Ortszeit), auf den sich Fristen und der Live-Vergleich beziehen. */
    standTag: string;
    planungsphase: {
        vorhanden: boolean;
        status: PlanungStatus | null;
        lph: number | null;
        katalogVersion: number | null;
    };
    lesefehler: SteckbriefLesefehler;
    projekt: {
        nummer: Angabe<string>;
        name: Angabe<string>;
        adresse: Angabe<string>;
    };
    bauherr: {
        name: Angabe<string>;
        anschrift: Angabe<string>;
        telefon: Angabe<string>;
        email: Angabe<string>;
    };
    baubeschreibung: Angabe<string>;
    bauaufgabe: Angabe<string>;
    /** Entscheid E1 — ISO-Tage (YYYY-MM-DD). */
    ausfuehrung: {
        beginn: Angabe<string>;
        ende: Angabe<string>;
    };
    /** Entscheid E2 — allein aus den Merkmalen. */
    umfang: {
        arbeitstage: Angabe<number>;
        maxBeschaeftigte: Angabe<number>;
        personentage: Angabe<number>;
        mehrereArbeitgeber: Angabe<boolean>;
        /** Punkt 9 der Vorankuendigung — dreiwertig wie `mehrereArbeitgeber`. */
        unternehmerOhneBeschaeftigte: Angabe<boolean>;
    };
    /** Gebaeudetyp/Nutzung aus den Merkmalen — waehlt die UfsA-Vorlage. */
    gebaeudetyp: Angabe<Gebaeudetyp>;
    /** Der Dritte nach § 4 BaustellV (Vorankuendigung P4, Erlassender der
     *  Bauordnung). `ungeklaert` heisst hier nur „nicht erfasst" — ob es
     *  keinen Dritten gibt, sagt die Antwort auf PL-A-02. */
    dritter: {
        name: Angabe<string>;
        anschrift: Angabe<string>;
        rolle: Angabe<string>;
    };
    /** Die Merkmale roh und dreiwertig, unveraendert durchgereicht (Kopie). */
    merkmale: ProjektMerkmale;
    /** Alle Pflichten nach BaustellV, dreiwertig mit Begruendung (`pflichtenStand`). */
    pflichten: PflichtEintrag[];
    anhang2: {
        /** Zutreffende Nummern (aufsteigend); `ungeklaert`, solange eine Nummer offen ist. */
        nummern: Angabe<AnhangIINummer[]>;
        /** Nummern, die weder bejaht noch ausgeschlossen sind. */
        offen: AnhangIINummer[];
        /** Die zehn Zeilen der Live-Pruefung mit Begruendung. */
        treffer: Anhang2Treffer[];
        /** Ids fuer `vorankuendigung.selectedDangerousWorks` (ueber die Bruecke). */
        vorankuendigungIds: DangerousWorkId[];
    };
    /** Fragen je Zielobjekt, sortiert wie in der Maske. Jeder Schluessel ist belegt (ggf. leer). */
    fragen: Record<FrageZielobjekt, SteckbriefFrage[]>;
    /** Relevante Zeilen der Unterlagen-Checkliste (Standardfilter „Relevant"). */
    unterlagen: SteckbriefUnterlage[];
    /** `widerspruch`, wenn der gespeicherte Schnappschuss nicht mehr zu den Merkmalen passt. */
    schnappschuss: Angabe<SchnappschussStand>;
}

// ═══════════════════════════════════════════════════════════════════════════
// HILFEN
// ═══════════════════════════════════════════════════════════════════════════

const text = (v: unknown): string | null => {
    if (typeof v !== 'string') return null;
    const t = v.trim();
    return t ? t : null;
};

const zahl = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null;

/**
 * Tagesdatum als ISO-Tag (YYYY-MM-DD) — aus ISO (auch mit Zeitanteil) oder
 * TT.MM.JJJJ, wie die Stammdaten es gepflegt haben koennen. Reines Zerlegen,
 * kein `toISOString`: das verschoebe den Tag je Zeitzone. `parseDateLocal`
 * prueft nur die Plausibilitaet.
 */
const alsIsoTag = (roh: string): string | null => {
    const s = roh.trim();
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
        const tag = `${iso[1]}-${iso[2]}-${iso[3]}`;
        return parseDateLocal(tag) ? tag : null;
    }
    const de = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (de) {
        const tag = `${de[3]}-${de[2].padStart(2, '0')}-${de[1].padStart(2, '0')}`;
        return parseDateLocal(tag) ? tag : null;
    }
    return null;
};

/** ISO-Tag → TT.MM.JJJJ durch Zerlegen; leer bei unbrauchbarer Eingabe. */
const tagDeutsch = (iso: string | null | undefined): string => {
    const t = String(iso || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
    const [j, m, d] = t.split('-');
    return `${d}.${m}.${j}`;
};

/** Grund, warum aus der Planungsphase nichts kommt — `null`, wenn sie da ist. */
const phasenGrund = (q: SteckbriefQuellen): string | null => {
    if (q.phase) return null;
    return q.lesefehler.planungsphase ? GRUND.phaseUnlesbar(q.lesefehler.planungsphase) : GRUND.keinePhase;
};

/** Textangabe aus den Projektdaten. */
const projektText = (metadata: Partial<ReportMetadata> | null, wert: unknown, leer = false): Angabe<string> => {
    if (!metadata) return ungeklaert<string>(null, QUELLE.keine, GRUND.projektdatenFehlen);
    const t = text(wert);
    if (!t || leer) return ungeklaert<string>(null, QUELLE.projektdaten, GRUND.nichtGepflegt);
    return belegt(t, QUELLE.projektdaten);
};

/** Zahl aus den Merkmalen (E2). */
const merkmalZahl = (q: SteckbriefQuellen, wert: unknown): Angabe<number> => {
    const grund = phasenGrund(q);
    if (grund) return ungeklaert<number>(null, QUELLE.planungMerkmale, grund);
    const z = zahl(wert);
    return z === null
        ? ungeklaert<number>(null, QUELLE.planungMerkmale, GRUND.merkmalFehlt)
        : belegt(z, QUELLE.planungMerkmale);
};

/** Dreiwertiges Merkmal: ein ausdrueckliches Nein ist belegt, Fehlen ungeklaert. */
const merkmalJaNein = (q: SteckbriefQuellen, wert: unknown): Angabe<boolean> => {
    const grund = phasenGrund(q);
    if (grund) return ungeklaert<boolean>(null, QUELLE.planungMerkmale, grund);
    return typeof wert === 'boolean'
        ? belegt(wert, QUELLE.planungMerkmale)
        : ungeklaert<boolean>(null, QUELLE.planungMerkmale, GRUND.merkmalFehlt);
};

/** Textfeld aus den Merkmalen (Dritter nach § 4) — getrimmt, leer = ungeklaert. */
const merkmalText = (q: SteckbriefQuellen, wert: unknown): Angabe<string> => {
    const grund = phasenGrund(q);
    if (grund) return ungeklaert<string>(null, QUELLE.planungMerkmale, grund);
    const t = text(wert);
    return t ? belegt(t, QUELLE.planungMerkmale) : ungeklaert<string>(null, QUELLE.planungMerkmale, GRUND.merkmalFehlt);
};

const dritterAngaben = (q: SteckbriefQuellen, d: DritterNachPara4 | undefined): ProjektSteckbrief['dritter'] => ({
    name: merkmalText(q, d?.name),
    anschrift: merkmalText(q, d?.anschrift),
    rolle: merkmalText(q, d?.rolle),
});

/**
 * Ein Datum des Ausfuehrungszeitraums (E1): Ausfuehrung vor Projektlaufzeit.
 * Ein gepflegtes, aber unlesbares Datum bleibt `ungeklaert` mit dem rohen
 * Text als Grund — still auf die Laufzeit zurueckzufallen hiesse, eine
 * Tippfehler-Bauzeit durch eine falsche zu ersetzen.
 */
const ausfuehrungsDatum = (
    metadata: Partial<ReportMetadata> | null,
    ausfuehrung: string | undefined,
    laufzeit: string | undefined,
): Angabe<string> => {
    if (!metadata) return ungeklaert<string>(null, QUELLE.keine, GRUND.projektdatenFehlen);
    const a = text(ausfuehrung);
    if (a) {
        const tag = alsIsoTag(a);
        return tag
            ? belegt(tag, QUELLE.projektAusfuehrung)
            : ungeklaert<string>(null, QUELLE.projektAusfuehrung, GRUND.datumUnlesbar(a));
    }
    const l = text(laufzeit);
    if (l) {
        const tag = alsIsoTag(l);
        return tag
            ? ungeklaert<string>(tag, QUELLE.projektLaufzeit, GRUND.rueckfallLaufzeit)
            : ungeklaert<string>(null, QUELLE.projektLaufzeit, GRUND.datumUnlesbar(l));
    }
    return ungeklaert<string>(null, QUELLE.projektdaten, GRUND.nichtGepflegt);
};

const ZIELOBJEKTE: readonly FrageZielobjekt[] = ['sigeplan', 'vorankuendigung', 'unterlage', 'ausschreibung', 'keins'];

const fragenJeZielobjekt = (fragen: readonly Planungsfrage[]): Record<FrageZielobjekt, SteckbriefFrage[]> => {
    const ergebnis = {} as Record<FrageZielobjekt, SteckbriefFrage[]>;
    ZIELOBJEKTE.forEach(z => { ergebnis[z] = []; });
    sortiereFragen([...fragen]).forEach(f => {
        const ziel: FrageZielobjekt = ZIELOBJEKTE.includes(f.zielobjekt) ? f.zielobjekt : 'keins';
        ergebnis[ziel].push({
            id: f.id,
            code: f.code,
            frage: f.frage,
            kritikalitaet: f.kritikalitaet,
            status: f.status,
            antwort: f.antwort || '',
            uebernommen_am: f.uebernommen_am || null,
        });
    });
    return ergebnis;
};

/**
 * Relevante Zeilen der Unterlagen-Checkliste. Ohne Planungsphase gibt es keine
 * Checkliste — dann leer; `planungsphase.vorhanden` sagt dem Leser, warum.
 */
const unterlagenListe = (q: SteckbriefQuellen): SteckbriefUnterlage[] => {
    if (!q.phase) return [];
    const merkmale = q.phase.merkmale || {};
    const eintraege = q.phase.unterlagen || [];
    return baueUnterlagenZeilen({ merkmale, eintraege, sigeSchritte: q.sigeSchritte })
        .filter(z => z.relevant)
        .map(z => ({
            id: z.id,
            name: z.name,
            quelle: z.quelle,
            gruppe: z.gruppe,
            status: z.status,
            dokument_id: z.eintrag?.dokument_id || null,
            relevanz: z.relevanz,
            erforderlich: z.erforderlich,
            ufsa: z.dokument?.ufsa === true,
            asplan: z.dokument?.asplan || null,
            lieferant: [...z.lieferant],
            sigeSchritte: [...z.sigeSchritte],
        }));
};

/**
 * Ids fuer Punkt 11 der Vorankuendigung. Nr. 1 hat in der Vorankuendigung
 * zwei Ids (Versinken, Absturz); die Bruecke liefert beide und ueberlaesst
 * die Wahl den Zahlen — hier faellt sie: Grabentiefe ueber 5 m → Versinken,
 * Absturzhoehe ueber 7 m → Absturz. Dieselben Schwellen wie `pruefeAnhang2`.
 */
const vorankuendigungIds = (nummern: readonly AnhangIINummer[], merkmale: ProjektMerkmale): DangerousWorkId[] => {
    const tiefe = zahl(merkmale.grabentiefeM);
    const hoehe = zahl(merkmale.absturzhoeheM);
    return idsFuerNummern(nummern).filter(id => {
        if (id === 'versinken') return tiefe !== null && tiefe > 5;
        if (id === 'absturz') return hoehe !== null && hoehe > 7;
        return true;
    });
};

// ═══════════════════════════════════════════════════════════════════════════
// AUFBAU
// ═══════════════════════════════════════════════════════════════════════════

export const baueSteckbrief = (quellen: SteckbriefQuellen, jetztIso: string): ProjektSteckbrief => {
    const q: SteckbriefQuellen = {
        ...quellen,
        fragen: quellen.fragen || [],
        sigeSchritte: quellen.sigeSchritte || [],
        lesefehler: quellen.lesefehler || OHNE_LESEFEHLER,
    };
    const standTag = String(jetztIso || '').slice(0, 10);
    const meta = q.metadata;
    const global = meta?.globalData;
    const phase = q.phase;
    const grund = phasenGrund(q);
    const merkmale: ProjektMerkmale = { ...(phase?.merkmale || {}) };
    // Der Vermerk „Rest bleibt offen" des Leitfadens steht im selben jsonb,
    // ist aber keine Projektangabe. Im Steckbrief truege ihn der allgemeine
    // Merkmalsauszug (`merkmaleAuszug` liest alle Schluessel) in SiGe-Plan,
    // Bauordnung und Handbuch — der Aenderungsdienst meldete dann eine
    // Abweichung, die keine ist, und die Bauordnung froere ihn mit ein.
    delete merkmale.leitfadenRestOffen;

    // ─── Baubeschreibung und Bauaufgabe: Planungsphase vor Projektdaten ───
    const beschreibungPhase = text(phase?.baubeschreibung);
    const beschreibungProjekt = text(meta?.projectDescription);
    let baubeschreibung: Angabe<string>;
    if (beschreibungPhase) {
        baubeschreibung = belegt(beschreibungPhase, QUELLE.planungGrundlagen);
    } else if (beschreibungProjekt) {
        baubeschreibung = ungeklaert(beschreibungProjekt, QUELLE.projektdaten, grund ? `${GRUND.nurProjektdaten} ${grund}` : GRUND.nurProjektdaten);
    } else {
        baubeschreibung = ungeklaert<string>(null, QUELLE.planungGrundlagen, grund || GRUND.grundlageFehlt);
    }
    const aufgabePhase = text(phase?.bauaufgabe);
    const bauaufgabe: Angabe<string> = aufgabePhase
        ? belegt(aufgabePhase, QUELLE.planungGrundlagen)
        : ungeklaert<string>(null, QUELLE.planungGrundlagen, grund || GRUND.grundlageFehlt);

    // ─── Umfang (E2) ─────────────────────────────────────────────────────
    const mehrereArbeitgeber = merkmalJaNein(q, merkmale.mehrereArbeitgeber);

    // ─── Gebaeudetyp — nur bekannte Werte, sonst ungeklaert ──────────────
    const typ = gebaeudetypAus(merkmale.gebaeudetyp);
    let gebaeudetyp: Angabe<Gebaeudetyp>;
    if (grund) gebaeudetyp = ungeklaert<Gebaeudetyp>(null, QUELLE.planungMerkmale, grund);
    else if (typ) gebaeudetyp = belegt(typ, QUELLE.planungMerkmale);
    else gebaeudetyp = ungeklaert<Gebaeudetyp>(null, QUELLE.planungMerkmale, GRUND.merkmalFehlt);

    // ─── Anhang II live ──────────────────────────────────────────────────
    const treffer = pruefeAnhang2(merkmale);
    const nummernListe = treffer
        .filter(t => t.zutreffend === true)
        .map(t => t.nummer)
        .filter(istAnhangIINummer)
        .sort((a, b) => a - b);
    const offen = treffer
        .filter(t => t.zutreffend === undefined)
        .map(t => t.nummer)
        .filter(istAnhangIINummer)
        .sort((a, b) => a - b);
    let nummern: Angabe<AnhangIINummer[]>;
    if (grund) nummern = ungeklaert<AnhangIINummer[]>(nummernListe, QUELLE.planungPruefung, grund);
    else if (offen.length) nummern = ungeklaert<AnhangIINummer[]>(nummernListe, QUELLE.planungPruefung, GRUND.anhang2Offen(offen));
    else nummern = belegt(nummernListe, QUELLE.planungPruefung);

    // ─── Schnappschuss gegen Live-Stand ──────────────────────────────────
    let schnappschuss: Angabe<SchnappschussStand>;
    if (!phase) {
        schnappschuss = ungeklaert<SchnappschussStand>('fehlt', QUELLE.planungPruefung, grund || GRUND.keinePhase);
    } else if (!phase.pruefung) {
        schnappschuss = ungeklaert<SchnappschussStand>('fehlt', QUELLE.planungPruefung, GRUND.pruefungNieGespeichert);
    } else if (pruefungWeichtAb(phase, jetztIso)) {
        schnappschuss = widerspruch<SchnappschussStand>('weicht_ab', QUELLE.planungPruefung, GRUND.pruefungWeichtAb(tagDeutsch(phase.pruefung.berechnet_am)));
    } else {
        schnappschuss = belegt<SchnappschussStand>('aktuell', QUELLE.planungPruefung);
    }

    return {
        standTag,
        planungsphase: {
            vorhanden: !!phase,
            status: phase?.status || null,
            lph: typeof phase?.lph === 'number' ? phase.lph : null,
            katalogVersion: typeof phase?.katalog_version === 'number' ? phase.katalog_version : null,
        },
        lesefehler: { ...q.lesefehler },
        projekt: {
            nummer: projektText(meta, meta?.projectNumber),
            name: projektText(meta, meta?.projectName),
            adresse: projektText(meta, meta?.projectAddress, istAdresseLeer(meta?.projectAddress)),
        },
        bauherr: {
            name: projektText(meta, meta?.clientName),
            anschrift: projektText(meta, meta?.clientAddress, istAdresseLeer(meta?.clientAddress)),
            telefon: projektText(meta, meta?.clientPhone),
            email: projektText(meta, meta?.clientEmail),
        },
        baubeschreibung,
        bauaufgabe,
        ausfuehrung: {
            beginn: ausfuehrungsDatum(meta, global?.executionStartDate, global?.startDate),
            ende: ausfuehrungsDatum(meta, global?.executionEndDate, global?.endDate),
        },
        umfang: {
            arbeitstage: merkmalZahl(q, merkmale.arbeitstage),
            maxBeschaeftigte: merkmalZahl(q, merkmale.maxBeschaeftigte),
            personentage: merkmalZahl(q, merkmale.personentage),
            mehrereArbeitgeber,
            unternehmerOhneBeschaeftigte: merkmalJaNein(q, merkmale.unternehmerOhneBeschaeftigte),
        },
        gebaeudetyp,
        dritter: dritterAngaben(q, merkmale.dritterNachPara4),
        merkmale,
        pflichten: pflichtenStand(phase, jetztIso),
        anhang2: {
            nummern,
            offen,
            treffer,
            vorankuendigungIds: vorankuendigungIds(nummernListe, merkmale),
        },
        fragen: fragenJeZielobjekt(q.fragen),
        unterlagen: unterlagenListe(q),
        schnappschuss,
    };
};

/** Der Eintrag einer Pflicht, oder `undefined`, wenn `pflichtenStand` sie nicht fuehrt. */
export const pflichtAus = (sb: ProjektSteckbrief, art: PflichtArt): PflichtEintrag | undefined =>
    sb.pflichten.find(p => p.art === art);

/** Dreiwertiger Stand einer Pflicht; `ungeklaert`, wenn sie nicht gefuehrt wird. */
export const pflichtStandAusSteckbrief = (sb: ProjektSteckbrief, art: PflichtArt): PflichtStand =>
    pflichtAus(sb, art)?.stand ?? 'ungeklaert';

/** Angaben, die nicht `belegt` sind — fuer die Leiste „Aus der Planungsphase" und die Ampel. */
export const offeneAngaben = (sb: ProjektSteckbrief): { pfad: string; angabe: Angabe<unknown> }[] => {
    const liste: { pfad: string; angabe: Angabe<unknown> }[] = [];
    const pruefe = (pfad: string, a: Angabe<unknown>) => { if (a.stand !== 'belegt') liste.push({ pfad, angabe: a }); };
    pruefe('projekt.nummer', sb.projekt.nummer);
    pruefe('projekt.name', sb.projekt.name);
    pruefe('projekt.adresse', sb.projekt.adresse);
    pruefe('bauherr.name', sb.bauherr.name);
    pruefe('bauherr.anschrift', sb.bauherr.anschrift);
    pruefe('bauherr.telefon', sb.bauherr.telefon);
    pruefe('bauherr.email', sb.bauherr.email);
    pruefe('baubeschreibung', sb.baubeschreibung);
    pruefe('bauaufgabe', sb.bauaufgabe);
    pruefe('ausfuehrung.beginn', sb.ausfuehrung.beginn);
    pruefe('ausfuehrung.ende', sb.ausfuehrung.ende);
    pruefe('umfang.arbeitstage', sb.umfang.arbeitstage);
    pruefe('umfang.maxBeschaeftigte', sb.umfang.maxBeschaeftigte);
    pruefe('umfang.personentage', sb.umfang.personentage);
    pruefe('umfang.mehrereArbeitgeber', sb.umfang.mehrereArbeitgeber);
    pruefe('umfang.unternehmerOhneBeschaeftigte', sb.umfang.unternehmerOhneBeschaeftigte);
    pruefe('gebaeudetyp', sb.gebaeudetyp);
    // `dritter` fehlt hier bewusst: die meisten Vorhaben haben keinen Dritten
    // nach § 4 — ein leeres Feld ist dort der Normalfall, keine Luecke.
    pruefe('anhang2.nummern', sb.anhang2.nummern);
    pruefe('schnappschuss', sb.schnappschuss);
    return liste;
};

// ═══════════════════════════════════════════════════════════════════════════
// AUSZUG JE FOLGEUNTERLAGE
// ═══════════════════════════════════════════════════════════════════════════

export type Folgeunterlage = 'vorankuendigung' | 'sigeplan' | 'asplan' | 'ufsa' | 'bauordnung' | 'handbuch';

export const FOLGEUNTERLAGEN: readonly Folgeunterlage[] = ['vorankuendigung', 'sigeplan', 'asplan', 'ufsa', 'bauordnung', 'handbuch'];

/** Welche Pflichten je Unterlage in den Auszug gehoeren (Matrix im Dateikopf). */
const PFLICHTEN_JE_UNTERLAGE: Record<Folgeunterlage, readonly PflichtArt[]> = {
    vorankuendigung: ['vorankuendigung'],
    sigeplan: ['sigeplan', 'koordinator'],
    asplan: ['asplan'],
    ufsa: ['ufsa'],
    bauordnung: ['koordinator', 'sigeplan'],
    handbuch: ['koordinator', 'sigeplan'],
};

/** Merkmale, die der A+S-Plan liest (Bestand, Schadstoffe, Boden) — beide
 *  Verdachtswege nach E4. */
const ASPLAN_MERKMALE: readonly (keyof ProjektMerkmale)[] =
    ['bestand', 'bestandBaujahr', 'abbruch', 'erdarbeiten', 'gefahrstoffeAnhang2', 'altlastenverdacht'];

/**
 * Die Umfangsgroessen stehen im Auszug unter `umfang`, nicht unter
 * `merkmale`. Sonst truege jede Unterlage, die nur die Bauaufgabe liest
 * (Bauordnung: bedingte Kapitel), die Beschaeftigtenzahl mit — und der
 * Aenderungsdienst meldete dort eine Abweichung, die sie nicht betrifft.
 * `unternehmerOhneBeschaeftigte` gehoert dazu: eine Angabe fuer Punkt 9 der
 * Vorankuendigung, keine Bauaufgabe.
 */
const UMFANG_MERKMALE: readonly (keyof ProjektMerkmale)[] =
    ['arbeitstage', 'maxBeschaeftigte', 'personentage', 'mehrereArbeitgeber', 'unternehmerOhneBeschaeftigte'];

/** Nie im allgemeinen Merkmalsauszug: Personendaten des Dritten nach § 4
 *  stehen nur im Feld `dritter` der Unterlagen, die ihn brauchen. */
const NUR_GEZIELT: readonly (keyof ProjektMerkmale)[] = ['dritterNachPara4'];

const wert = <T>(a: Angabe<T>): T | null => a.wert;

const nachCode = <T extends { code: string }>(liste: readonly T[]): T[] =>
    [...liste].sort((a, b) => a.code.localeCompare(b.code, 'de', { numeric: true }));

const nachId = <T extends { id: string }>(liste: readonly T[]): T[] =>
    [...liste].sort((a, b) => a.id.localeCompare(b.id, 'de', { numeric: true }));

const istSchlichtesObjekt = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Schluessel rekursiv sortieren, `undefined` streichen. Listen behalten ihre
 * Reihenfolge — die ist oben schon festgelegt (Code, Id, Nummer).
 */
const stabil = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(stabil);
    if (!istSchlichtesObjekt(v)) return v;
    const aus: Record<string, unknown> = {};
    Object.keys(v).sort().forEach(k => {
        const w = v[k];
        if (w !== undefined) aus[k] = stabil(w);
    });
    return aus;
};

const projektAuszug = (sb: ProjektSteckbrief) => ({
    nummer: wert(sb.projekt.nummer),
    name: wert(sb.projekt.name),
    adresse: wert(sb.projekt.adresse),
});

const bauherrAuszug = (sb: ProjektSteckbrief, mitKontakt: boolean) => ({
    name: wert(sb.bauherr.name),
    anschrift: wert(sb.bauherr.anschrift),
    ...(mitKontakt ? { telefon: wert(sb.bauherr.telefon), email: wert(sb.bauherr.email) } : {}),
});

const ausfuehrungAuszug = (sb: ProjektSteckbrief) => ({
    beginn: wert(sb.ausfuehrung.beginn),
    ende: wert(sb.ausfuehrung.ende),
});

const umfangAuszug = (sb: ProjektSteckbrief) => ({
    arbeitstage: wert(sb.umfang.arbeitstage),
    maxBeschaeftigte: wert(sb.umfang.maxBeschaeftigte),
    personentage: wert(sb.umfang.personentage),
    mehrereArbeitgeber: wert(sb.umfang.mehrereArbeitgeber),
});

/** Merkmale der Bauaufgabe — ohne die Umfangsgroessen (siehe `UMFANG_MERKMALE`)
 *  und ohne den Dritten (`NUR_GEZIELT`); `nur` schraenkt weiter ein. */
const merkmaleAuszug = (sb: ProjektSteckbrief, nur?: readonly (keyof ProjektMerkmale)[]) => {
    const aus: Partial<ProjektMerkmale> = {};
    const schluessel = (nur || (Object.keys(sb.merkmale) as (keyof ProjektMerkmale)[]))
        .filter(k => !UMFANG_MERKMALE.includes(k) && !NUR_GEZIELT.includes(k));
    schluessel.forEach(k => {
        const v = sb.merkmale[k];
        if (v !== undefined) (aus as Record<string, unknown>)[k] = v;
    });
    return aus;
};

/** Der Dritte nach § 4 — nur Werte; `null` je Feld, wenn nichts erfasst. */
const dritterAuszug = (sb: ProjektSteckbrief) => ({
    name: wert(sb.dritter.name),
    anschrift: wert(sb.dritter.anschrift),
    rolle: wert(sb.dritter.rolle),
});

const anhang2Auszug = (sb: ProjektSteckbrief, mitIds: boolean) => ({
    nummern: [...(sb.anhang2.nummern.wert || [])],
    offen: [...sb.anhang2.offen],
    ...(mitIds ? { vorankuendigungIds: [...sb.anhang2.vorankuendigungIds] } : {}),
});

const pflichtenAuszug = (sb: ProjektSteckbrief, arten: readonly PflichtArt[]): Record<string, PflichtStand> => {
    const aus: Record<string, PflichtStand> = {};
    arten.forEach(a => { aus[a] = pflichtStandAusSteckbrief(sb, a); });
    return aus;
};

const fragenAuszug = (liste: readonly SteckbriefFrage[]) =>
    nachCode(liste).map(f => ({ code: f.code, status: f.status, antwort: f.antwort }));

const unterlagenAuszug = (liste: readonly SteckbriefUnterlage[]) =>
    nachId(liste).map(u => ({ id: u.id, name: u.name, status: u.status, dokument_id: u.dokument_id }));

/**
 * Serialisierbarer Auszug fuer EINE Folgeunterlage — nur Werte, Schluessel
 * sortiert, Listen geordnet. Zwei Aufrufe mit inhaltlich gleichem Steckbrief
 * liefern denselben `JSON.stringify`. Die Matrix steht im Dateikopf.
 */
export const steckbriefAuszug = (sb: ProjektSteckbrief, unterlage: Folgeunterlage): Record<string, unknown> => {
    let roh: Record<string, unknown>;
    switch (unterlage) {
        case 'vorankuendigung':
            roh = {
                projekt: projektAuszug(sb),
                bauherr: bauherrAuszug(sb, true),
                dritter: dritterAuszug(sb),
                baubeschreibung: wert(sb.baubeschreibung),
                ausfuehrung: ausfuehrungAuszug(sb),
                umfang: {
                    maxBeschaeftigte: wert(sb.umfang.maxBeschaeftigte),
                    mehrereArbeitgeber: wert(sb.umfang.mehrereArbeitgeber),
                    unternehmerOhneBeschaeftigte: wert(sb.umfang.unternehmerOhneBeschaeftigte),
                },
                anhang2: anhang2Auszug(sb, true),
                pflichten: pflichtenAuszug(sb, PFLICHTEN_JE_UNTERLAGE.vorankuendigung),
                fragen: fragenAuszug(sb.fragen.vorankuendigung),
            };
            break;
        case 'sigeplan':
            roh = {
                projekt: projektAuszug(sb),
                bauherr: bauherrAuszug(sb, false),
                baubeschreibung: wert(sb.baubeschreibung),
                bauaufgabe: wert(sb.bauaufgabe),
                ausfuehrung: ausfuehrungAuszug(sb),
                umfang: umfangAuszug(sb),
                merkmale: merkmaleAuszug(sb),
                anhang2: anhang2Auszug(sb, false),
                pflichten: pflichtenAuszug(sb, PFLICHTEN_JE_UNTERLAGE.sigeplan),
                fragen: fragenAuszug(sb.fragen.sigeplan),
                unterlagen: unterlagenAuszug(sb.unterlagen),
            };
            break;
        case 'asplan':
            roh = {
                projekt: projektAuszug(sb),
                bauherr: bauherrAuszug(sb, false),
                baubeschreibung: wert(sb.baubeschreibung),
                merkmale: merkmaleAuszug(sb, ASPLAN_MERKMALE),
                pflichten: pflichtenAuszug(sb, PFLICHTEN_JE_UNTERLAGE.asplan),
                unterlagen: unterlagenAuszug(sb.unterlagen.filter(u => u.asplan !== null)),
            };
            break;
        case 'ufsa':
            roh = {
                projekt: projektAuszug(sb),
                bauherr: bauherrAuszug(sb, false),
                baubeschreibung: wert(sb.baubeschreibung),
                ausfuehrung: ausfuehrungAuszug(sb),
                gebaeudetyp: wert(sb.gebaeudetyp),
                pflichten: pflichtenAuszug(sb, PFLICHTEN_JE_UNTERLAGE.ufsa),
                fragen: fragenAuszug(sb.fragen.unterlage),
                unterlagen: unterlagenAuszug(sb.unterlagen.filter(u => u.ufsa)),
            };
            break;
        case 'bauordnung':
            roh = {
                projekt: projektAuszug(sb),
                bauherr: bauherrAuszug(sb, false),
                dritter: dritterAuszug(sb),
                ausfuehrung: ausfuehrungAuszug(sb),
                umfang: { mehrereArbeitgeber: wert(sb.umfang.mehrereArbeitgeber) },
                merkmale: merkmaleAuszug(sb),
                pflichten: pflichtenAuszug(sb, PFLICHTEN_JE_UNTERLAGE.bauordnung),
            };
            break;
        case 'handbuch':
            roh = {
                projekt: projektAuszug(sb),
                bauherr: bauherrAuszug(sb, false),
                ausfuehrung: ausfuehrungAuszug(sb),
                merkmale: merkmaleAuszug(sb),
                anhang2: anhang2Auszug(sb, true),
                pflichten: pflichtenAuszug(sb, PFLICHTEN_JE_UNTERLAGE.handbuch),
                unterlagen: unterlagenAuszug(sb.unterlagen),
            };
            break;
    }
    return stabil(roh) as Record<string, unknown>;
};
